"use client"

import { cn } from "@/lib/utils"

/**
 * Secondary badge rendered alongside the invoice ``StatusBadge`` when the
 * customer still owes a step before Stripe can collect — an in-session
 * 3-D Secure challenge, or an out-of-band voucher / bank transfer
 * (support-stripe-require-action-status §5.7).
 *
 * It never replaces the status chip, it qualifies it. The status itself
 * is "Payment pending" (or, for a deposit whose *balance* leg owes the
 * step, "Partially Paid" — which is exactly why the flag is derived from
 * the legs too and not from the status alone). Amber ``bg-warning/10
 * text-warning`` — the needs-attention family used by ``overdue`` /
 * ``email_failed`` — so it reads as a call to action next to the calm
 * blue pending chip.
 *
 * Lives in ``components/`` beside ``status-badge.tsx`` rather than in
 * ``features/invoices/`` because the customer portal shows the same state
 * in its own voice: ``label`` carries the wording ("Customer action
 * required" for the rep, "Action needed" for the customer) while the two
 * surfaces stay locked to one palette.
 */
export function ActionRequiredBadge({
  label = "Customer action required",
  className,
  testId = "action-required-badge",
}: {
  label?: string
  className?: string
  testId?: string
}) {
  return (
    <span
      data-testid={testId}
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded text-caption font-medium bg-warning/10 text-warning",
        className,
      )}
    >
      {label}
    </span>
  )
}
